import * as THREE from 'three';
import { gsap } from 'gsap';

const hexToRgb = (hex) => {
    const bigint = parseInt(hex.slice(1), 16);
    return {
        r: ((bigint >> 16) & 255) / 255,
        g: ((bigint >> 8) & 255) / 255,
        b: (bigint & 255) / 255
    };
}

const degToRad = (deg) => THREE.MathUtils.degToRad(deg)

export default class AiRevolutionTeam {
    constructor(el) {
        if(this.isMobile()) return;
        this.el = el;
        this.parent = this.el.closest('.watch-video');
        this.animationScheme = this.el.dataset.revolutionTeam || '0';
        this.sizes = {};
        this.firstRender = true;
        this.setSizes();
        this.initScene();
        this.initCamera();
        this.initRenderer();
        this.calcPano();
        this.init();
        this.initRenderTargets();
        this.initFinalPlane();
        this.initEvents();
        this.animate();
    }

    isMobile() {
        return window.matchMedia("(max-width: 767px)").matches
    }

    initScene() {
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0xffffff);
    }

    setSizes() {
        this.sizes.width = this.el.clientWidth;
        this.sizes.height = this.el.clientHeight;
        this.sizes.aspect = this.el.clientWidth / this.el.clientHeight;
        this.sizes.dpi = Math.min(window.devicePixelRatio, 2);
        this.sizes.sceneWidth = 7.650360016605446;
        this.sizes.sceneHeight = 4.663076581549986;
        this.sizes.rendererWidth = 1680;
        this.sizes.rendererHeight = 1024;
    }

    calcPano() {
        this.pano = {};
        this.pano.vFOV = THREE.MathUtils.degToRad(this.camera.fov);
        this.pano.height = 2 * Math.tan(this.pano.vFOV / 2) * this.camera.position.z;
        this.pano.width = this.pano.height * this.camera.aspect;
        this.pano.em = this.pano.width / this.sizes.width;

        this.finalPano = {};
        this.finalPano.vFOV = THREE.MathUtils.degToRad(this.finalCamera.fov);
        this.finalPano.height = 2 * Math.tan(this.finalPano.vFOV / 2) * this.finalCamera.position.z;
        this.finalPano.width = this.finalPano.height * this.finalCamera.aspect;
        this.finalPano.em = this.finalPano.width / this.sizes.width;
    }

    initRenderer() {
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true
        });
        this.renderer.setSize(this.sizes.width, this.sizes.height);
        this.renderer.setPixelRatio(this.sizes.dpi);
        this.renderer.setClearColor(0x000000, 0); // Set clear color to transparent
        this.el.appendChild(this.renderer.domElement);
    }

    initCamera() {
        this.camera = new THREE.PerspectiveCamera(50, this.sizes.sceneWidth / this.sizes.sceneHeight, 0.1, 1000);
        this.camera.position.z = 5;

        this.finalCamera = new THREE.PerspectiveCamera(50, this.sizes.aspect, 0.1, 1000);
        this.finalCamera.position.z = 5;

        switch (this.animationScheme) {
            case '0':
                this.camera.position.set(0.5, 0, 3);
                break;
            case '1':
                this.camera.position.set(1.5, -1, 7);
                this.camera.rotation.set(0, 0, degToRad(180));
                break;
            case '2':
                this.camera.position.set(1, 1.5, 5);
                this.camera.rotation.set(0, 0, degToRad(90));
                break;
            case '3':
                this.camera.position.set(1, -1, 5);
                break;
            case '4':
                this.camera.position.set(1, 1, 5);
                break;
            case '5':
                this.camera.position.set(0, 1, 5);
                break;
        }
    }

    init() {
        const sceneWidth = this.sizes.sceneWidth;
        const sceneHeight = this.sizes.sceneHeight;

        this.objects = [
            { // 0
                size: { width: 1.6, height: 1.6 },
                position: { x: 0, y: 0, z: 0 },
                rotation: { x: 0, y: 0, z: 0 },
                gradient: {
                    colors: [
                        {stop: 0, color: '#00F3FF'},
                        {stop: 0.184058, color: '#0086B9'},
                        {stop: 0.225, color: '#00AABD'},
                        {stop: 0.26, color: '#00B1BD'},
                        {stop: 0.29, color: '#00B7BE'},
                        {stop: 0.31, color: '#00BABE'},
                        {stop: 0.349108, color: '#00C2BF'},
                        {stop: 0.494224, color: '#53B5BE'},
                        {stop: 0.619507, color: '#0077BC'},
                        {stop: 0.759898, color: '#004EBB'},
                        {stop: 0.911597, color: '#00AAF9'},
                        {stop: 0.975, color: '#007FCA'}
                    ],
                    startPoint: { x: .9, y: 0 },
                    endPoint: { x: 0, y: .9 },
                    blend: 0
                },
            },
            { // 1
                size: { width: 1.6, height: 1.6 },
                position: { x: 0, y: 0, z: 0 },
                rotation: { x: 0, y: 0, z: 0 },
                gradient: {
                    colors: [
                        {stop: 0, color: '#00F3FF'},
                        {stop: 0.184058, color: '#0086B9'},
                        {stop: 0.225, color: '#00AABD'},
                        {stop: 0.26, color: '#00B1BD'},
                        {stop: 0.29, color: '#00B7BE'},
                        {stop: 0.31, color: '#00BABE'},
                        {stop: 0.349108, color: '#00C2BF'},
                        {stop: 0.494224, color: '#53B5BE'},
                        {stop: 0.619507, color: '#0077BC'},
                        {stop: 0.759898, color: '#004EBB'},
                        {stop: 0.911597, color: '#00AAF9'},
                        {stop: 0.975, color: '#007FCA'}
                    ],
                    startPoint: { x: .9, y: 0 },
                    endPoint: { x: 0, y: .9 },
                    blend: 0
                },
            },
            { // 2
                size: { width: 1.5, height: .9 },
                position: { x: -sceneWidth * .1, y: -sceneHeight * .2, z: 0 },
                rotation: { x: 0, y: 0, z: 15 },
                pivot: { x: -1, y: -1 },
                gradient: {
                    colors: [
                        {stop: 0, color: '#0086B9'},
                        {stop: 0.494224, color: '#53B5BE'},
                        {stop: 0.619507, color: '#0077BC'},
                        {stop: 0.759898, color: '#004EBB'},
                        {stop: 0.911597, color: '#00AAF9'},
                        {stop: 0.975, color: '#007FCA'}
                    ],
                    startPoint: { x: 0, y: -0.2 },
                    endPoint: { x: 0.2, y: 1.15 },
                    blend: 1
                },
            },
            { // 3
                size: { width: 1.5, height: .8 },
                position: { x: sceneWidth * .2, y: 0, z: 0 },
                rotation: { x: 0, y: 0, z: -35 },
                pivot: { x: 1, y: 1 },
                gradient: {
                    colors: [
                        {stop: 0, color: '#00F3FF'},
                        {stop: 0.184058, color: '#0086B9'},
                        {stop: 0.225, color: '#00AABD'},
                        {stop: 0.26, color: '#00B1BD'},
                        {stop: 0.29, color: '#00B7BE'},
                        {stop: 0.31, color: '#00BABE'},
                        {stop: 0.349108, color: '#00C2BF'},
                        {stop: 0.494224, color: '#53B5BE'},
                        {stop: 0.619507, color: '#0077BC'},
                        {stop: 0.759898, color: '#004EBB'},
                        {stop: 0.911597, color: '#00AAF9'},
                        {stop: 0.975, color: '#007FCA'}
                    ],
                    startPoint: {x: -0.4, y: 0.3},
                    endPoint: {x: 0.2, y: 1.4},
                    blend: 1,
                },
            },

            {
                size: { width: 1.5, height: .9 },
                position: { x: -sceneWidth * .1, y: -sceneHeight * 9.5, z: 0 },
                // position: { x: -this.pano.width * .1, y: -this.pano.height * .2, z: 0 },
                rotation: { x: 0, y: 0, z: 15 },
                gradient: {
                    colors: [
                        {stop: 0, color: '#0086B9'},
                        {stop: 0.494224, color: '#53B5BE'},
                        {stop: 0.619507, color: '#0077BC'},
                        {stop: 0.759898, color: '#004EBB'},
                        {stop: 0.911597, color: '#00AAF9'},
                        {stop: 0.975, color: '#007FCA'}
                    ],
                    startPoint: { x: 0, y: -0.2 },
                    endPoint: { x: 0.2, y: 1.15 },
                    blend: 1
                },
            },

            {
                size: { width: 1.5, height: .8 },
                position: { x: sceneWidth * 3, y: 0, z: 0 },
                // position: { x: this.pano.width * .2, y: 0, z: 0 },
                rotation: { x: 0, y: 0, z: -35 },
                gradient: {
                    colors: [
                        {stop: 0, color: '#00F3FF'},
                        {stop: 0.184058, color: '#0086B9'},
                        {stop: 0.225, color: '#00AABD'},
                        {stop: 0.26, color: '#00B1BD'},
                        {stop: 0.29, color: '#00B7BE'},
                        {stop: 0.31, color: '#00BABE'},
                        {stop: 0.349108, color: '#00C2BF'},
                        {stop: 0.494224, color: '#53B5BE'},
                        {stop: 0.619507, color: '#0077BC'},
                        {stop: 0.759898, color: '#004EBB'},
                        {stop: 0.911597, color: '#00AAF9'},
                        {stop: 0.975, color: '#007FCA'}
                    ],
                    startPoint: {x: -0.4, y: 0.3},
                    endPoint: {x: 0.2, y: 1.4},
                    blend: 1,
                },
            },
        ];

        this.initObjects();
        this.initAnimations();
    }

    initAnimations() {
        this.timeline = gsap.timeline({ repeat: -1, paused: true });

        const sceneWidth = this.sizes.sceneWidth;
        const sceneHeight = this.sizes.sceneHeight;

        this.timeline
            .fromTo(this.objects[1].group.rotation, { z: degToRad(0) }, { z: degToRad(90), duration: 30, ease: 'none' }, 0)
            .fromTo(this.objects[1].mesh.scale, { y: 1 }, { y: 1.8, duration: 30, ease: 'none' }, 0)

            .fromTo(this.objects[1].group.rotation, { z: degToRad(90) }, { z: degToRad(360), duration: 85, ease: 'none' }, 30)
            .fromTo(this.objects[1].mesh.scale, { y: 1.8 }, { y: 1, duration: 85, ease: 'none' }, 30)

            .fromTo(this.objects[2].group.rotation, { z: degToRad(0) }, { z: degToRad(40), duration: 105, ease: 'none' }, 0)

            .fromTo(this.objects[3].group.rotation, { z: degToRad(0) }, { z: degToRad(-53), duration: 105, ease: 'none' }, 0)

            .fromTo(this.objects[4].mesh.position, { y: -sceneHeight * 1.5 }, { y: -sceneHeight * .2, duration: 105, ease: 'none' }, 10)

            .fromTo(this.objects[5].mesh.position, { x: sceneWidth * 1 }, { x: sceneWidth * .2, duration: 105, ease: 'none' }, 10)
            .fromTo(this.objects[5].mesh.position, { y: sceneHeight * .9 }, { y: 0, duration: 105, ease: 'none' }, 10)
            .fromTo(this.objects[5].group.rotation, { z: degToRad(70) }, { z: degToRad(0), duration: 105, ease: 'none' }, 10)

        this.timeline.timeScale(5)

        const position = Math.floor(Math.random() * 10) + 1;
        this.timeline.seek(position);
    }

    initRenderTargets() {
        this.renderTargets = this.objects.map(() =>
            new THREE.WebGLRenderTarget(this.sizes.rendererWidth, this.sizes.rendererHeight, {
                format: THREE.RGBAFormat,
                type: THREE.UnsignedByteType,
                minFilter: THREE.LinearFilter,
                magFilter: THREE.LinearFilter,
                generateMipmaps: false,
                anisotropy: this.renderer.capabilities.getMaxAnisotropy(),
                antialias: true,
            })
        );
    }

    initFinalPlane() {
        const geometry = new THREE.PlaneGeometry(this.sizes.sceneWidth * 1.7, this.sizes.sceneHeight * 1.7);
        const material = this.createCombinedShaderMaterial();
        this.finalPlane = new THREE.Mesh(geometry, material);
        this.scene.add(this.finalPlane);
    }

    createCombinedShaderMaterial() {
        const vertexShader = this.getVertexShader();
        const fragmentShader = this.getCombinedFragmentShader();
        const uniforms = {};

        this.renderTargets.forEach((rt, index) => {
            uniforms[`texture${index}`] = { value: rt.texture };
        });

        return new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            transparent: true,
        });
    }

    getCombinedFragmentShader() {
        const textureCount = this.objects.length;
        let samplerDeclarations = '';
        let textureBlending = '';

        // Generate sampler declarations
        for (let i = 0; i < textureCount; i++) {
            samplerDeclarations += `uniform sampler2D texture${i};\n`;
        }

        // Start with the first texture
        textureBlending = `finalColor = texture2D(texture0, vUv);\n`;

        // Add blending for subsequent textures
        for (let i = 1; i < textureCount; i++) {
            textureBlending += `
    vec4 texColor${i + 1} = texture2D(texture${i}, vUv);
    diffuseColor = texColor${(i + 1)};
    if(diffuseColor.a > 0.9) {
    ${!this.objects[i].gradient.blend ? `finalColor = mix(finalColor, diffuseColor, diffuseColor.a);` : ''}
    ${this.objects[i].gradient.blend ? `finalColor = vec4(min(finalColor.rgb, diffuseColor.rgb / diffuseColor.a), 1.);` : ''}
    }`;
        }

        return `
${samplerDeclarations}
varying vec2 vUv;

void main() {
    vec4 finalColor;
    vec4 diffuseColor;
    vec4 whiteColor = vec4(1.0);
    
    ${textureBlending}
    
    gl_FragColor = finalColor;
}`;
    }

    createShaderMaterial(object) {
        const vertexShader = this.getVertexShader();
        const fragmentShader = this.getGradientShader(object.gradient.colors);
        const uniforms = {
            startPoint: { value: new THREE.Vector2(object.gradient.startPoint.x, object.gradient.startPoint.y) },
            endPoint: { value: new THREE.Vector2(object.gradient.endPoint.x, object.gradient.endPoint.y) },
        };

        const material = new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            transparent: true,
        });

        return material;
    }

    initObjects() {
        this.objectScenes = this.objects.map(() => new THREE.Scene());

        const sceneWidth = this.sizes.sceneWidth;
        const sceneHeight = this.sizes.sceneHeight;

        this.objects.forEach((object, index) => {
            const width = sceneWidth * object.size.width;
            const height = sceneHeight * object.size.height;
            this.objects[index].geometry = new THREE.PlaneGeometry(width, height);
            this.objects[index].material = this.createShaderMaterial(object);
            this.objects[index].mesh = new THREE.Mesh(object.geometry, object.material);
            this.objects[index].mesh.position.set(object.position.x, object.position.y, object.position.z);
            this.objects[index].mesh.rotation.set(THREE.MathUtils.degToRad(object.rotation.x), THREE.MathUtils.degToRad(object.rotation.y), THREE.MathUtils.degToRad(object.rotation.z));
            this.objects[index].group = new THREE.Group();
            if(object.pivot) {
                const pivot = new THREE.Vector3(object.pivot.x * width, object.pivot.y * height, 0);
                this.objects[index].mesh.position.sub(pivot);
                this.objects[index].group.position.add(pivot);
            }
            // this.objects[index].group.rotation.set(THREE.MathUtils.degToRad(object.rotation.x), THREE.MathUtils.degToRad(object.rotation.y), THREE.MathUtils.degToRad(object.rotation.z));
            this.objects[index].group.add(this.objects[index].mesh);
            this.objectScenes[index].add(this.objects[index].group);
        });
    }

    getGradientShader(gradient) {
        gradient.sort((a, b) => a.stop - b.stop);

        const colorArrays = gradient.map(stop => {
            const color = hexToRgb(stop.color);
            return `vec4(${color.r}, ${color.g}, ${color.b}, 1.0)`;
        });

        const stopArrays = gradient.map(stop => stop.stop.toFixed(6));

        const getGradientColorFunc = `
        vec4 getGradientColor(float t) {
            vec4 colors[${gradient.length}];
            float stops[${gradient.length}];
            
            ${colorArrays.map((color, i) => `colors[${i}] = ${color};`).join('\n    ')}
            
            ${stopArrays.map((stop, i) => `stops[${i}] = ${stop};`).join('\n    ')}
            
            for (int i = 0; i < ${gradient.length - 1}; i++) {
                if (t >= stops[i] && t < stops[i + 1]) {
                    float mixFactor = (t - stops[i]) / (stops[i + 1] - stops[i]);
                    return mix(colors[i], colors[i + 1], mixFactor);
                }
            }
            
            return colors[${gradient.length - 1}];
        }`;

        return `
        varying vec2 vUv;
        
        uniform vec2 startPoint;
        uniform vec2 endPoint;
        
        ${getGradientColorFunc}
        
        void main() {
            vec2 start = startPoint;
            vec2 end = endPoint;
            
            start.y = 1.0 - start.y;
            end.y = 1.0 - end.y;
            
            vec2 dt = end - start;
            vec2 pt = vUv - start;
            
            float t = dot(pt, dt) / dot(dt, dt);
            t = clamp(t, 0.0, 1.0);
            vec4 color = getGradientColor(t);
            gl_FragColor = color;
        }`;
    }

    getVertexShader() {
        return `
        varying vec2 vUv;
        
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`;
    }

    initEvents() {
        this.parent.addEventListener('mouseenter', () => {
            this.timeline.play();
            this.isAnimating = true;
        })

        this.parent.addEventListener('mouseleave', () => {
            this.timeline.pause();
            this.isAnimating = false;
        })

        window.addEventListener('resize', this.onResize.bind(this));
    }

    animate() {
        requestAnimationFrame(this.animate.bind(this));

        if(!this.firstRender && !this.isAnimating) {
            return
        }

        this.firstRender = false;

        // rotate final camera
        if(this.animationScheme == 0) {
            // this.finalCamera.rotation.z += 0.001;
        }

        if(this.animationScheme == 1) {
            // this.finalCamera.rotation.z -= 0.001;
        }

        if(this.animationScheme == 2) {
            // this.finalCamera.rotation.z += 0.001;
        }

        // Render each object to its own render target
        this.objects.forEach((object, index) => {
            this.renderer.setRenderTarget(this.renderTargets[index]);
            this.renderer.clear();
            this.renderer.render(this.objectScenes[index], this.camera);
        });

        // Render the final combined result
        this.renderer.setRenderTarget(null);
        this.renderer.clear();
        this.renderer.render(this.scene, this.finalCamera);
    }

    onResize() {
        this.setSizes();
        this.renderer.setSize(this.sizes.width, this.sizes.height);
        this.renderer.setPixelRatio(this.sizes.dpi);
        this.renderer.render(this.scene, this.finalCamera);
    }
}
